import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {Backend, PERMISSION} from '@generated-backend-types';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {ActivatedRoute, Router} from '@angular/router';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {of, switchMap, takeUntil, throwError} from 'rxjs';
import {EditModeEnum} from '../../../../enums/EditModeEnum';
import {UserSessionService} from '../../../../services/user-session.service';
import {ToastrService} from 'ngx-toastr';
import {NotEnoughPermissionsError} from '../../../../errors/NotEnoughPermissionsError';
import {HttpErrorResponse} from '@angular/common/http';
import {FormErrorService} from '../../../../services/form-error.service';
import {StateCardComponent} from '../../../../components/state-card/state-card.component';
import {TeamMemberService} from '../../../../services/api/administration/team-member.service';
import {DeleteCardComponent} from '../../../../components/form-inputs/delete-card/delete-card.component';
import {IfHasAllPermissionsDirective} from '../../../../directives/if-has-all-permissions.directive';
import TeamMember = Backend.App.Resources.Dashboard.Administration.TeamMember;
import TeamMemberData = Backend.App.Data.Dashboard.Administration.TeamMemberData;
import ModelStateEnum = Backend.App.Enums.ModelStateEnum;

@Component({
  selector: 'app-authenticated-administration-team-members-edit',
  templateUrl: './edit.component.html',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    StateCardComponent,
    DeleteCardComponent,
    IfHasAllPermissionsDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public teamMember?: TeamMember;
  public mode = EditModeEnum.CREATE;

  public form: StrictFormGroup<TeamMemberData> = this.fb.group({
    state: this.fb.control(ModelStateEnum.DRAFT, [Validators.required]),
    first_name: this.fb.control('', [Validators.required]),
    last_name: this.fb.control('', [Validators.required]),
    hello_one_page_slug: this.fb.control(''),
  });

  public readonlyFormValues: StrictFormGroup<{ext_team_member_id: string}> = this.fb.group({
    ext_team_member_id: this.fb.control({value: this.translate.instant('authenticated.administration.team-members.edit.form.ext_team_member_id.empty'), disabled: true}),
  })

  protected readonly ModelStateEnum = ModelStateEnum;
  protected readonly PERMISSION = PERMISSION;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    private readonly userSessionService: UserSessionService,
    private readonly teamMemberService: TeamMemberService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly formErrorService: FormErrorService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const teamMember: TeamMember | undefined = data['teamMember'];

      if (teamMember) {
        this.teamMember = teamMember;
        this.form.patchValue(teamMember);
        this.mode = EditModeEnum.EDIT;
        this.cdr.markForCheck();

        this.readonlyFormValues.controls.ext_team_member_id.setValue(teamMember.ext_team_member_id);
      }

      this.titleService.setTitle('authenticated.administration.team-members.' + this.mode + '.title');
    });
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (!this.form.valid) {
      return;
    }

    const data = this.form.value as TeamMemberData;

    of(this.teamMember?.id).pipe(
      switchMap(id => {
        if (id) {
          if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_TEAMMEMBER_UPDATE_ALL])) {
            throwError(() => new NotEnoughPermissionsError());
          }

          return this.teamMemberService.update(id, data);
        }

        if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_TEAMMEMBER_CREATE_ALL])) {
          throwError(() => new NotEnoughPermissionsError());
        }

        return this.teamMemberService.create(data);
      })
    ).subscribe({
      next: res => {
        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.toastrService.success(this.translate.instant(`authenticated.administration.team-members.${this.mode}.success`));
        this.teamMember = res.data;

        if (this.mode === EditModeEnum.CREATE) {
          this.router.navigateByUrl(`/administration/team-members/${this.teamMember.id}`).then();
        }

        this.mode = this.teamMember?.id ? EditModeEnum.EDIT : EditModeEnum.CREATE;
        this.cdr.markForCheck();
      },
      error: err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.form.markAllAsTouched();
          this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
          this.formErrorService.mapErrorToForm(this.form, err);
        } else {
          this.toastrService.error(this.translate.instant(`authenticated.administration.team-members.${this.mode}.error`));
        }
      },
    })

    this.cdr.markForCheck();
  }

  public deleteItem() {
    if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_TEAMMEMBER_DELETE_ALL])) {
      this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
    }

    if (this.teamMember) {
      this.teamMemberService.delete(this.teamMember.id).subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('authenticated.administration.team-members.delete.success'));
          this.router.navigateByUrl('/administration/team-members').then();
        },
        error: () => {
          this.toastrService.error(this.translate.instant('authenticated.administration.team-members.delete.error'));
        }
      });
    }
  }
}
